'use client'

/**
 * Context provider for Apple AI consent gating across app-beta.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AIConsentModal } from '@/components/compliance/AIConsentModal'
import { isAiConsentRequiredError } from '@/lib/compliance/aiConsentConstants'

type AiConsentContextValue = {
  granted: boolean | null
  loading: boolean
  refresh: () => Promise<void>
  /** Returns true if consent is active; shows modal and waits if not. */
  ensureConsent: () => Promise<boolean>
  grantConsent: () => Promise<boolean>
  revokeConsent: () => Promise<boolean>
}

const AiConsentContext = createContext<AiConsentContextValue | null>(null)

export function useAiConsentGate(): AiConsentContextValue {
  const ctx = useContext(AiConsentContext)
  if (!ctx) {
    throw new Error('useAiConsentGate must be used within AiConsentProvider')
  }
  return ctx
}

/** Safe hook for components that may render outside provider (returns no-op gate). */
export function useAiConsentGateOptional(): AiConsentContextValue | null {
  return useContext(AiConsentContext)
}

export function AiConsentProvider({ children }: { children: React.ReactNode }) {
  const [granted, setGranted] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const pendingResolveRef = useRef<((value: boolean) => void) | null>(null)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/ai-consent', { credentials: 'include' })
      if (res.status === 401) {
        setGranted(false)
        return
      }
      if (!res.ok) {
        setGranted(false)
        return
      }
      const data = (await res.json()) as { granted?: boolean }
      setGranted(!!data.granted)
    } catch {
      setGranted(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const grantConsent = useCallback(async () => {
    try {
      const res = await fetch('/api/ai-consent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'grant' }),
      })
      if (!res.ok) return false
      const data = (await res.json()) as { granted?: boolean }
      setGranted(!!data.granted)
      return !!data.granted
    } catch {
      return false
    }
  }, [])

  const revokeConsent = useCallback(async () => {
    try {
      const res = await fetch('/api/ai-consent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'revoke' }),
      })
      if (!res.ok) return false
      const data = (await res.json()) as { granted?: boolean }
      setGranted(!!data.granted)
      return true
    } catch {
      return false
    }
  }, [])

  const closeModal = useCallback((result: boolean) => {
    setModalOpen(false)
    const resolve = pendingResolveRef.current
    pendingResolveRef.current = null
    resolve?.(result)
  }, [])

  const ensureConsent = useCallback(async (): Promise<boolean> => {
    // Always re-check server so revoke (other tab / API) is honored before AI calls.
    try {
      const res = await fetch('/api/ai-consent', { credentials: 'include' })
      if (res.ok) {
        const data = (await res.json()) as { granted?: boolean }
        if (data.granted) {
          setGranted(true)
          return true
        }
        setGranted(false)
      }
    } catch {
      // fall through to modal
    }

    return new Promise<boolean>((resolve) => {
      pendingResolveRef.current = resolve
      setModalOpen(true)
    })
  }, [])

  const handleAgree = useCallback(async () => {
    const ok = await grantConsent()
    closeModal(ok)
  }, [grantConsent, closeModal])

  const handleDecline = useCallback(() => {
    closeModal(false)
  }, [closeModal])

  const handleLearnMore = useCallback(() => {
    window.open('https://wagamour.com/privacy#ai-powered-features', '_blank', 'noopener,noreferrer')
  }, [])

  return (
    <AiConsentContext.Provider
      value={{
        granted,
        loading,
        refresh,
        ensureConsent,
        grantConsent,
        revokeConsent,
      }}
    >
      {children}
      <AIConsentModal
        isOpen={modalOpen}
        onAgree={handleAgree}
        onDecline={handleDecline}
        onLearnMore={handleLearnMore}
      />
    </AiConsentContext.Provider>
  )
}

export { isAiConsentRequiredError }
